/**
 * Agency Agent – Dubai Police 999 Emergency Assistant
 *
 * Asks user for consent per agency (one at a time):
 * - Dubai Police (police)
 * - Ambulance (ambulance)
 * - Civil Defense (civil_defense)
 *
 * After consent collection, calls finalize_report which:
 *   1. Saves agency tags to the backend
 *   2. Gets the incident number
 *   3. Stores incident number in localStorage for IncidentCardAgent
 *
 * Then calls move_to_incident_card — IncidentCardAgent will show the card and
 * deliver the calming message.
 * Labels are NEVER cleared when transitioning.
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';
import { flagConversation } from '../utils/flagConversation';

export function createAgencyAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'agency_agent',
    name: 'Agency Reporting Agent',
    description: 'Collects consent for each emergency agency, flags the conversation, then routes to incident card',

    initialPrompt: 'Ask consent for each recommended agency ONE AT A TIME in the user\'s language. Then call finalize_report with confirmed agencies. Read the incident number clearly. Then call move_to_incident_card().',

    entryGuardrails: requireLanguage(),

    exitGuardrails: {
      transitions: new Map([
        ['to_incident_card', { target: { agentId: 'incident_card_agent' }, guardrails: [languageDetectedGuardrail] }],
        ['to_human_handoff', { target: { agentId: 'human_handoff_agent' }, guardrails: [languageDetectedGuardrail] }],
      ]),
      fallback: { agentId: 'incident_card_agent' },
    },

    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {

      // ─── finalize_report ────────────────────────────────────────────────
      if (toolName === 'finalize_report') {
        const confirmedAgencies: string[] = args.confirmed_agencies || [];
        console.log('[AgencyAgent] Report finalized – agencies:', confirmedAgencies);

        // Persist confirmed agencies in transitionData so move_to_incident_card
        // can read them correctly even if called in a later tool turn.
        context.transitionData.confirmedAgencies = confirmedAgencies;

        let incidentNumber: string | null = null;
        if (confirmedAgencies.length > 0) {
          incidentNumber = await flagConversation(confirmedAgencies);
        }

        return {
          toolName, args,
          result: {
            success: true,
            confirmed_agencies: confirmedAgencies,
            incident_number: incidentNumber,
            message: confirmedAgencies.length > 0
              ? `Report flagged for: ${confirmedAgencies.join(', ')}. Incident reference number: ${incidentNumber ?? 'N/A'}. Read the incident number clearly to the caller. Then silently move to the incident card.`
              : 'No agencies flagged. Silently move to the incident card.',
          },
        };
      }

      // ─── move_to_incident_card ──────────────────────────────────────────
      if (toolName === 'move_to_incident_card' || toolName === 'move_to_closing' || toolName === 'return_to_start') {
        const confirmedAgencies: string[] = context.transitionData.confirmedAgencies || [];
        console.log('[AgencyAgent] Moving to incident card — confirmed agencies preserved:', confirmedAgencies);

        return {
          toolName, args,
          result: { success: true, message: 'Moving to incident card agent' },
          triggersTransition: true,
          transitionTo: 'incident_card_agent',
          transitionData: {
            confirmedAgencies,
            incidentSummary: context.transitionData.incidentSummary || '',
          },
        };
      }

      // ─── request_human_handoff ──────────────────────────────────────────
      if (toolName === 'request_human_handoff') {
        return {
          toolName, args,
          result: { success: true },
          triggersTransition: true,
          transitionTo: 'human_handoff_agent',
          transitionData: { confirmedAgencies: context.transitionData.confirmedAgencies || [] },
        };
      }

      return { toolName, args, result: { success: false, error: 'Unknown tool' } };
    },

    onActivate: async (context: AgentContext) => {
      console.log('[AgencyAgent] Activated – recommended agencies:', context.transitionData.recommendedAgencies);
    },
  };
}

export default createAgencyAgent;
